import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, TIME_PERIODS } from '../lib/utils';
import { Plus, Trash2, Pencil, Filter, TrendingDown, Calendar as CalendarRange, Clock, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import { ExpenseFormDialog } from '../components/finance/ExpenseFormDialog';
import { StatusBadge } from '../components/finance/RequisitionsTab';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format, startOfDay, startOfWeek, startOfMonth, startOfYear, endOfDay, endOfWeek, endOfMonth, endOfYear, isWithinInterval, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_LABELS = {
  personnel: 'Personnel',
  administrative: 'Administrative',
  project: 'Project',
  women_project: 'Women Project',
};
const GROUP_BADGE = {
  personnel: 'bg-blue-100 text-blue-700',
  administrative: 'bg-slate-100 text-slate-700',
  project: 'bg-purple-100 text-purple-700',
  women_project: 'bg-pink-100 text-pink-700',
};

const ExpensesPage = () => {
  const { hasRole } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // expense item pending delete confirmation
  const [filterGroup, setFilterGroup] = useState('all');
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [viewingReceipt, setViewingReceipt] = useState(null);

  useEffect(() => {
    fetchExpenses();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGroup]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects`);
      setProjects(res.data);
    } catch (e) { /* noop */ }
  };

  const fetchExpenses = async () => {
    try {
      let url = `${API_URL}/api/expenses`;
      if (filterGroup && filterGroup !== 'all') {
        url += `?category_group=${filterGroup}`;
      }
      const response = await axios.get(url);
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (expense) => {
    setEditingExpense(expense);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    // window.confirm is unreliable on some mobile browsers — use AlertDialog
    try {
      await axios.delete(`${API_URL}/api/expenses/${id}`);
      toast.success('Expense deleted');
      setDeleteTarget(null);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  // Get date range for current period
  const getDateRange = (period) => {
    const now = new Date();
    let startDate, endDate, label;
    
    switch (period) {
      case 'daily':
        startDate = startOfDay(now);
        endDate = endOfDay(now);
        label = format(now, 'EEEE, MMMM d, yyyy');
        break;
      case 'weekly':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        label = `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
        break;
      case 'monthly':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        label = format(now, 'MMMM yyyy');
        break;
      case 'yearly':
        startDate = startOfYear(now);
        endDate = endOfYear(now);
        label = format(now, 'yyyy');
        break;
      default:
        return { startDate: null, endDate: null, label: 'All Time' };
    }
    
    return { startDate, endDate, label };
  };

  // Filter expenses by time period
  const getFilteredExpenses = (period) => {
    const { startDate, endDate } = getDateRange(period);
    
    if (!startDate || !endDate) return expenses;
    
    return expenses.filter(item => {
      const itemDate = parseISO(item.date);
      return isWithinInterval(itemDate, { start: startDate, end: endDate });
    });
  };

  const { label: periodLabel } = getDateRange(timePeriod);
  const filteredExpenses = getFilteredExpenses(timePeriod);
  const totalExpenses = filteredExpenses.reduce((sum, item) => sum + item.amount, 0);
  const allTimeTotal = expenses.reduce((sum, item) => sum + item.amount, 0);
  const canEdit = hasRole(['admin', 'finance_officer']);

  // Group expenses by group/subcategory for chart
  const expensesByCategory = filteredExpenses.reduce((acc, item) => {
    const category = (item.subcategory || item.category || 'other').replace(/_/g, ' ');
    acc[category] = (acc[category] || 0) + item.amount;
    return acc;
  }, {});

  const chartData = Object.entries(expensesByCategory).map(([name, value]) => ({ name, value }));

  // Format datetime for display
  const formatDateTime = (dateStr, createdAt) => {
    const date = parseISO(dateStr);
    let timeStr = '';
    if (createdAt) {
      try {
        const created = parseISO(createdAt);
        timeStr = format(created, 'h:mm a');
      } catch (e) {
        timeStr = '';
      }
    }
    return {
      date: format(date, 'EEE, MMM d, yyyy'),
      time: timeStr
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Expense Management</h2>
          <p className="text-muted-foreground">Track all expenditures</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            path="exports/financial-report"
            filenameBase="suubi_financial_report"
            label="Export"
            testId="expense-export-btn"
          />
          {canEdit && (
            <Button className="btn-press" data-testid="add-expense-btn"
              onClick={() => { setEditingExpense(null); setDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Add Expense
            </Button>
          )}
        </div>
      </div>

      <ExpenseFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editingExpense}
        projects={projects}
        onSaved={fetchExpenses}
      />

      {/* Time Period Tabs with Date Range Display */}
      <div className="space-y-2">
        <Tabs value={timePeriod} onValueChange={setTimePeriod} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-4" data-testid="expense-period-tabs">
            {TIME_PERIODS.map((period) => (
              <TabsTrigger key={period.value} value={period.value} data-testid={`expense-period-${period.value}`}>
                {period.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 text-sm">
          <CalendarRange className="h-4 w-4 text-red-500" />
          <span className="font-medium text-red-500">{periodLabel}</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/20">
                <TrendingDown className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground capitalize">{timePeriod} Expenses</p>
                <p className="text-3xl font-heading font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
                <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-900/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/20">
                <CalendarRange className="h-8 w-8 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">All Time Total</p>
                <p className="text-3xl font-heading font-bold text-orange-600">{formatCurrency(allTimeTotal)}</p>
                <p className="text-xs text-muted-foreground mt-1">{expenses.length} total records</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses by Category Chart */}
      {chartData.length > 0 && (
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading capitalize">{timePeriod} Expenses by Category</CardTitle>
            <p className="text-sm text-muted-foreground">{periodLabel}</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="value" fill="#E53935" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={filterGroup} onValueChange={setFilterGroup}>
          <SelectTrigger className="w-52" data-testid="expense-filter">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(GROUP_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label} Expenses
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="ml-auto">
          {filteredExpenses.length} records for {periodLabel}
        </Badge>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Related Project</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Receipt</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {canEdit && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No expense records found for {periodLabel}
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((item) => {
                  const { date, time } = formatDateTime(item.date, item.created_at);
                  const projectName = item.related_project ? (projects.find(p => p.id === item.related_project)?.name || '—') : null;
                  return (
                    <TableRow key={item.id} className="table-row-hover">
                      <TableCell>
                        <div>
                          <p className="font-medium">{date}</p>
                          {time && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {time}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {item.category_group && (
                            <Badge className={`capitalize ${GROUP_BADGE[item.category_group] || ''}`} variant="outline">
                              {GROUP_LABELS[item.category_group] || item.category_group}
                            </Badge>
                          )}
                          <p className="text-xs text-muted-foreground capitalize">
                            {(item.subcategory || item.category || '').replace(/_/g, ' ')}
                            {item.women_activity ? ` · ${item.women_activity.replace(/_/g, ' ')}` : ''}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.description || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {projectName ? <Badge variant="secondary">{projectName}</Badge> : <span className="text-xs">—</span>}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={item.status || 'recorded'} />
                      </TableCell>
                      <TableCell>
                        {item.receipt_image ? (
                          <button
                            type="button"
                            onClick={() => setViewingReceipt(item.receipt_image)}
                            className="flex items-center gap-2 hover:underline"
                            data-testid={`view-receipt-${item.id}`}
                          >
                            <img src={item.receipt_image} alt="Receipt" className="h-10 w-10 object-cover rounded border" />
                            <Eye className="h-3 w-3 text-muted-foreground" />
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium text-red-600">
                        {formatCurrency(item.amount)}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEdit(item)}
                              data-testid={`edit-expense-${item.id}`}
                              className="h-8 px-2.5"
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteTarget(item)}
                              data-testid={`delete-expense-${item.id}`}
                              className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Receipt viewer dialog */}
      <Dialog open={!!viewingReceipt} onOpenChange={(o) => { if (!o) setViewingReceipt(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Receipt</DialogTitle>
            <DialogDescription>Tap and hold to save</DialogDescription>
          </DialogHeader>
          {viewingReceipt && (
            <img src={viewingReceipt} alt="Receipt" className="w-full max-h-[70vh] object-contain rounded border" />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — replaces unreliable window.confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-expense-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete expense?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>
                  Are you sure you want to delete{' '}
                  <strong>{formatCurrency(deleteTarget.amount)}</strong>{' '}
                  ({deleteTarget.category?.replace(/_/g, ' ')})
                  {deleteTarget.description ? ` — "${deleteTarget.description}"` : ''}?
                  This action cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-expense-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-expense-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesPage;
